import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
    TrashIcon,
    SparklesIcon,
    DocumentTextIcon,
    PhotoIcon,
    GlobeAltIcon,
    VideoCameraIcon,
    CloudArrowUpIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { agentInstanceService } from '../../../services/agentService';
import { getUser } from '../../../lib/storage';
import { tenantService } from '../../../services/tenantService';
import ConfirmationDialog from '../../../components/ui/ConfirmationDialog';
import { EyeIcon } from 'lucide-react';
import { fileService } from '../../../services/fileService';
import { fetchImageUrl } from '../../../helpers/helper';
import { useAuth } from '../../../context/AuthContext';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { SelectField, TextField } from '../../ui/Field';
import { Overlay } from '../../ui/Overlay';

const KnowledgeBaseTab: React.FC<{ agent: any }> = ({ agent }) => {
    type KBType = 'pdf' | 'text' | 'image' | 'url' | 'sitemap' | 'video';
    type KBStatus = 'pending' | 'completed' | 'failed';
    enum DisplayKBStatus {
        Pending = "processing",
        Completed = "processed",
        Failed = "error"
    }
    interface KBItem {
        id: string;
        agentInstanceId?: string | null;
        clientId?: string | null;
        name: string;
        type: KBType;
        size?: string;
        lastUpdated: string;
        embeddingStatus: KBStatus;
        remoteId?: string;
        remoteUrl?: string;
        /** Why processing failed — or, on a completed sitemap, why some pages were skipped. */
        errorMessage?: string;
        sitemapSummary?: SitemapSummary;
    }
    interface SitemapSummary {
        totalUrls: number;
        succeeded: number;
        failed: number;
        failedPages?: Array<{ url: string; error: string }>;
        sitemapErrors?: Array<{ url: string; error: string }>;
    }

    const [search, setSearch] = useState('');
    const [items, setItems] = useState<KBItem[]>([]);
    const [urlType, setUrlType] = useState<KBType>('url');
    const [urlValue, setUrlValue] = useState('');
    const [expandedErrorsId, setExpandedErrorsId] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [isAnyDocProcessing, setIsAnyDocProcessing] = useState(false);

    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingTrainId, setPendingTrainId] = useState<string | null>(null);
    const [retraining, setRetraining] = useState(false);
    const [tenantName, setTenantName] = useState<string>('');
    const [tenantId, setTenantId] = useState<string>('');
    const { isAuthenticated, isLoading } = useAuth();

    const fetchTenantName = async () => {
        try {
            const user = getUser() as any;
            const embeddedTenantName = user?.tenant?.name || user?.user?.tenant?.name;
            if (embeddedTenantName) {
                setTenantName(String(embeddedTenantName));
            }

            const tenantId = user?.tenantId || user?.user?.tenantId || localStorage.getItem('agentic_tenant_id');
            if (tenantId) setTenantId(String(tenantId));
            if (!tenantId || embeddedTenantName) return;

            const tenant = await tenantService.get(tenantId);
            const resolvedTenantName = (tenant as any)?.name || (tenant as any)?.tenantName || '';
            if (resolvedTenantName) setTenantName(String(resolvedTenantName));
        } catch (err) {
            console.error("Failed to load tenant name", err);
        }
    };

    useEffect(() => {
        if (!isLoading && isAuthenticated) {
            fetchTenantName();
        }
    }, [isAuthenticated, isLoading]);

    const stats = useMemo(() => {
        const total = items.length;
        const completed = items.filter((i) => i.embeddingStatus === 'completed').length;
        const pending = items.filter((i) => i.embeddingStatus === 'pending').length;
        const errors = items.filter((i) => i.embeddingStatus === 'failed').length;

        const anyProcessing = items.some((i) => i.embeddingStatus === 'pending');
        setIsAnyDocProcessing(anyProcessing);

        return { total, completed, pending, errors };
    }, [items]);

    useEffect(() => {
        let interval: NodeJS.Timeout | undefined;
        if (isAnyDocProcessing) {
            interval = setInterval(() => {
                fetchDocs();
            }, 5000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isAnyDocProcessing]);

    const updateItem = (id: string, patch: Partial<KBItem>) => {
        setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
    };

    function mapServerDocumentsToKBItems(resp: any): KBItem[] {
        if (!resp || !Array.isArray(resp.documents)) return [];

        return resp.documents.map((d: any) => {
            const name = d.originalFilename ?? d.title ?? (d.sourceUri ? d.sourceUri.split('/').pop()?.split('?')[0] : 'unknown');
            const remoteUrl = d.sourceUri ?? null;
            const remoteId = d.id ?? d.key ?? null;
            const clientName = d.clientName;
            const contentType = d.contentType ?? null;
            const lastUpdated = d.updatedAt ? new Date(d.updatedAt).toISOString().slice(0, 10) : (d.createdAt ? new Date(d.createdAt).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10));
            const type: KBType = (contentType && contentType.startsWith('image')) ? 'image' : (name?.toLowerCase().endsWith('.pdf') ? 'pdf' : d.sourceType === 'sitemap' ? 'sitemap' : 'text');
            // metadata.error can outlive a later successful retry on non-sitemap
            // documents, so only trust it while the document is failed. Sitemap
            // metadata is rewritten on every run, so its warning is current.
            const errorMessage = (d.embeddingStatus === 'failed' || d.sourceType === 'sitemap') && typeof d.metadata?.error === 'string'
                ? d.metadata.error
                : undefined;

            return {
                id: d.id,
                agentInstanceId: d.agentInstanceId || null,
                clientId: d.clientId || null,
                name: name,
                clientName,
                type,
                size: d.sizeBytes ? `${(d.sizeBytes / 1024 / 1024).toFixed(1)} MB` : undefined,
                lastUpdated,
                embeddingStatus: d.embeddingStatus === 'completed' ? 'completed' : (d.embeddingStatus === 'failed' ? 'failed' : 'pending'),
                remoteId,
                remoteUrl,
                s3ObjectData:d.s3ObjectData,
                errorMessage,
                sitemapSummary: d.sourceType === 'sitemap' ? d.metadata?.sitemap : undefined,
            } as KBItem;
        });
    }

    const removeDocument: any = async (documentId: string) => {
        try {
            if (!agent?.id) return;

            await agentInstanceService.deleteKBDocument(
                agent.id,
                documentId
            )
            toast.success('Document deleted');
            await fetchDocs();
        } catch (err) {
            if ((err as any)?.name === 'AbortError') return;
            console.error('Failed to delete document for agent', err);
            toast.error('Failed to delete document');
            throw err;
        }
    }

    // Failed documents: process again from the stored file/URL (the backend's train-document
    // endpoint reprocesses a failed document instead of re-linking it).
    const [retryingId, setRetryingId] = useState<string | null>(null);
    const retryFailedDocument = async (documentId: string) => {
        if (!agent?.id) return;
        setRetryingId(documentId);
        try {
            await agentInstanceService.trainDocument(agent.id, documentId);
            toast.success('Processing the document again');
            await fetchDocs();
        } catch (err) {
            if ((err as any)?.name === 'AbortError') return;
            console.error('Failed to retry document processing', err);
            toast.error('Could not retry processing');
        } finally {
            setRetryingId(null);
        }
    };

    const trainDocumentForAgentInstance = async (documentId: string) => {
        try {
            if (!agent?.id) return;

            await agentInstanceService.trainDocument(agent?.id, documentId);
            toast.success('Retrain started');
            await fetchDocs();
        } catch (err) {
            if ((err as any)?.name === 'AbortError') return;
            console.error('Failed to train document for agent', err);
            toast.error('Failed to start retrain');
            throw err;
        }
    };

    const fetchDocs = async () => {
        try {
            const resp = await agentInstanceService.listAgentInstanceDocuments(agent.id, { page: 1, limit: 50, search: search });
            const serverItems = mapServerDocumentsToKBItems(resp);

            setItems(prev => {
                const optimistic = prev.filter(it => it.id?.startsWith?.('up-'));
                const nonOptimistic = prev.filter(it => !it.id?.startsWith?.('up-'));
                const merged: KBItem[] = [];

                const serverByRemoteUrl = new Map<string, KBItem>();
                const serverByName = new Map<string, KBItem>();

                for (const s of serverItems) {
                    if (s.remoteUrl) serverByRemoteUrl.set((s.remoteUrl || '').split('?')[0], s);
                    serverByName.set(s.name, s);
                }

                const matchedServerIds = new Set<string | undefined>();

                for (const o of optimistic) {
                    const oUrl = o.remoteUrl ? o.remoteUrl.split('?')[0] : null;
                    let match: KBItem | undefined = undefined;
                    if (oUrl && serverByRemoteUrl.has(oUrl)) {
                        match = serverByRemoteUrl.get(oUrl);
                    } else if (serverByName.has(o.name)) {
                        match = serverByName.get(o.name);
                    }

                    if (match) {
                        merged.push(match);
                        matchedServerIds.add(match.remoteId ?? match.id);
                    } else {
                        merged.push(o);
                    }
                }

                for (const s of serverItems) {
                    if (!matchedServerIds.has(s.remoteId ?? s.id)) {
                        merged.push(s);
                    }
                }

                return merged;
            });

        } catch (err) {
            if ((err as any)?.name === 'AbortError') return;
            console.error('Failed to load agent documents', err);
        }
    };

    useEffect(() => {
        if (!agent?.id) return;
        const controller = new AbortController();

        const timeoutId = setTimeout(() => {
            fetchDocs();
        }, 500); // <-- debounce delay (change as needed)

        return () => {
            clearTimeout(timeoutId);  // clear old debounce
            controller.abort();       // cancel previous fetch
        };
    }, [agent?.id, search]);


    function normalizeUploadResponse(resp: any) {
        if (!resp) return [];

        const items = Array.isArray(resp.items) ? resp.items : (Array.isArray(resp.data) ? resp.data : []);

        return items.map((it: any) => ({
            name: it.originalName ?? it.name ?? it.fileName,
            key: it.key ?? it.path ?? it.id,
            url: it.publicUrl ?? it.url ?? it.fileUrl ?? it.presignedUrl,
            contentType: it.contentType ?? it.mimeType,
            bucket: it.bucket,
            etag: it.etag,
            raw: it,
        }));
    }

    const handleUploadFilesSequential = async (fileList: FileList | null) => {
        if (!fileList || fileList.length === 0) return;
        const now = new Date().toISOString().slice(0, 10);
        const files = Array.from(fileList);
        const baseTs = Date.now();

        const optimisticItems: KBItem[] = files.map((f, idx) => {
            let type: KBType = 'pdf';
            if (f.type.startsWith('image/')) type = 'image';
            else if (f.type === 'text/plain') type = 'text';
            else if (f.name.toLowerCase().endsWith('.pdf')) type = 'pdf';

            return {
                id: `up-${baseTs}-${idx}-${Math.random().toString(36).slice(2, 8)}-${f.name}`,
                name: f.name,
                type,
                size: `${(f.size / 1024 / 1024).toFixed(1)} MB`,
                lastUpdated: now,
                embeddingStatus: 'pending',
            };
        });

        setItems(prev => [...optimisticItems, ...prev]);

        const user = getUser();
        const ENV_TENANT_ID: any = user?.tenantId;

        for (let i = 0; i < files.length; i++) {
            const f = files[i];
            const optimistic = optimisticItems[i];

            try {
                const resp = await fileService.upload(f, {});
                const uploaded = normalizeUploadResponse(resp)[0] ?? null;

                const remoteId = uploaded?.key ?? uploaded?.etag ?? uploaded?.name ?? null;
                const remoteUrl = uploaded?.url ?? null;
                const bucket = uploaded?.bucket ?? null;
                const displayUrl = remoteUrl ? remoteUrl.split('?')[0] : null;

                toast.success(resp?.message || "Uploaded successfully!");

                updateItem(optimistic.id, {
                    embeddingStatus: 'pending',
                    remoteId,
                    remoteUrl,
                    name: uploaded?.name ?? optimistic.name,
                });
                fetchDocs();


                try {
                    const payload = {
                        sourceUri: remoteUrl,
                        s3ObjectData:{
                            key:remoteId,
                            bucket,
                            contentType:uploaded?.contentType 
                        },
                        originalFilename: uploaded?.name ?? f.name,
                        contentType: uploaded?.contentType ?? f.type ?? '',
                    };

                    const id = agent?.id;
                    const docResp = await agentInstanceService.AgentInstanceDocument(id, payload);
                    console.log('Document metadata saved:', docResp);

                } catch (metaErr) {
                    console.error('Failed to send document metadata', metaErr);
                    updateItem(optimistic.id, { embeddingStatus: 'failed' });
                }
            } catch (err) {
                console.error('Upload failed for', f.name, err);
                updateItem(optimistic.id, { embeddingStatus: 'failed' });
            }
        }
    };

    const onDropFiles = (files: FileList | null) => {
        handleUploadFilesSequential(files);
    };

    const addUrl = async () => {
        const value = urlValue.trim();
        if (!value) return;

        if (urlType !== 'sitemap') {
            toast.error(`Adding a ${urlType} source isn't supported yet.`);
            return;
        }

        const now = new Date().toISOString().slice(0, 10);
        // "up-" prefix matches the optimistic-item convention fetchDocs() dedupes against.
        const optimisticId = `up-sitemap-${Date.now()}`;
        setItems((prev) => [{ id: optimisticId, name: value, type: urlType, lastUpdated: now, embeddingStatus: 'pending', remoteUrl: value }, ...prev]);
        setUrlValue('');

        try {
            const payload = {
                sourceType: 'sitemap',
                sourceUri: value,
                title: value,
            };
            const id = agent?.id;
            await agentInstanceService.AgentInstanceDocument(id, payload);
            toast.success('Sitemap submitted for ingestion.');
            fetchDocs();
        } catch (err) {
            console.error('Failed to add sitemap knowledge-base source', err);
            const reason = (err as any)?.message;
            toast.error(reason ? `Failed to add sitemap: ${reason}` : 'Failed to add sitemap.');
            updateItem(optimisticId, { embeddingStatus: 'failed', errorMessage: reason });
        }
    };

    const TypeBadge: React.FC<{ type: KBType }> = ({ type }) => {
        const map: Record<KBType, { icon: React.ComponentType<any>; label: string }> = {
            pdf: { icon: DocumentTextIcon as any, label: 'PDF' },
            text: { icon: DocumentTextIcon as any, label: 'Text' },
            image: { icon: PhotoIcon as any, label: 'Image' },
            url: { icon: GlobeAltIcon as any, label: 'URL' },
            sitemap: { icon: GlobeAltIcon as any, label: 'Sitemap' },
            video: { icon: VideoCameraIcon as any, label: 'Video' },
        };
        const I = map[type].icon;
        return (
            <Badge className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-agentic-gray-100 text-agentic-text-secondary">
                <I className="h-3.5 w-3.5" /> {map[type].label}
            </Badge>
        );
    };

    const StatusBadge: React.FC<{ embeddingStatus: KBStatus }> = ({ embeddingStatus }) => (
        <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${embeddingStatus === 'completed'
                ? 'bg-agentic-accent-green/10 text-agentic-accent-green'
                : embeddingStatus === 'pending'
                    ? 'bg-agentic-accent-yellow/10 text-agentic-accent-yellow'
                    : 'bg-agentic-accent-red/10 text-agentic-accent-red'
                }`}
        >
            {embeddingStatus === "completed" ? DisplayKBStatus.Completed : embeddingStatus === "pending" ? DisplayKBStatus.Pending : DisplayKBStatus.Failed}
        </span>
    );

    /** Failure reason under a document row, plus an expandable per-page list for sitemaps. */
    const DocumentErrors: React.FC<{ doc: KBItem }> = ({ doc }) => {
        if (!doc.errorMessage || doc.embeddingStatus === 'pending') return null;

        const isWarning = doc.embeddingStatus === 'completed';
        const details = [...(doc.sitemapSummary?.sitemapErrors ?? []), ...(doc.sitemapSummary?.failedPages ?? [])];
        const hiddenCount = (doc.sitemapSummary?.failed ?? 0) - (doc.sitemapSummary?.failedPages?.length ?? 0);
        const expanded = expandedErrorsId === doc.id;

        return (
            <div className={`mt-1 text-xs max-w-2xl ${isWarning ? 'text-agentic-accent-yellow' : 'text-agentic-accent-red'}`}>
                <div className="break-words">
                    {isWarning ? 'Some pages were skipped: ' : 'Error: '}{doc.errorMessage}
                </div>
                {details.length > 0 && (
                    <>
                        <button
                            type="button"
                            className="underline mt-0.5"
                            onClick={() => setExpandedErrorsId(expanded ? null : doc.id)}
                        >
                            {expanded ? 'Hide details' : `Show details (${details.length})`}
                        </button>
                        {expanded && (
                            <ul className="mt-1 space-y-0.5 max-h-48 overflow-y-auto pr-2 text-agentic-text-secondary">
                                {details.map((d) => (
                                    <li key={d.url} className="break-all">
                                        <a href={d.url} target="_blank" rel="noreferrer" className="underline">{d.url}</a>
                                        {' — '}{d.error}
                                    </li>
                                ))}
                                {hiddenCount > 0 && <li>…and {hiddenCount} more</li>}
                            </ul>
                        )}
                    </>
                )}
            </div>
        );
    };

    const TrainingBadge: React.FC<{ id: string; agentInstanceId: string | null | undefined; onClick?: () => void }> = ({ id, agentInstanceId, onClick }) => (
        <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${agentInstanceId
                ? 'bg-agentic-accent-green/10 text-agentic-accent-green'
                : 'bg-agentic-accent-red/10 text-agentic-accent-red'
                }`}
            onClick={agentInstanceId ? undefined : onClick}
            style={{ cursor: agentInstanceId ? "unset" : "pointer" }}
        >
            {agentInstanceId ? "trained" : "not trained"}
        </span>
    );


    const handleTrainingClick = (documentId: string, embeddingStatus: KBStatus, agentInstanceId?: string | null) => {
        if (agentInstanceId) return;

        if (embeddingStatus !== "completed") {
            toast.error("Cannot train document for agent as it is not processed in vector db.")
            return
        }

        setPendingTrainId(documentId);
        setConfirmOpen(true);
    };

    const handleConfirmRetrain = async () => {
        if (!pendingTrainId) {
            setConfirmOpen(false);
            return;
        }
        setRetraining(true);
        setConfirmOpen(false);
        try {
            await trainDocumentForAgentInstance(pendingTrainId);
        } catch (e) { } finally {
            setRetraining(false);
            setPendingTrainId(null);
        }
    };

    const handleCancelRetrain = () => {
        setConfirmOpen(false);
        setPendingTrainId(null);
    };

    return (
        <>
            <div className="p-6 space-y-6">
                {/* stats cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-agentic-gray-50 rounded-lg p-4"><div className="text-2xl font-bold text-agentic-text-primary">{stats.total}</div><div className="text-sm text-agentic-text-secondary">Total Documents</div></div>
                    <div className="bg-agentic-gray-50 rounded-lg p-4"><div className="text-2xl font-bold text-agentic-accent-green">{stats.completed}</div><div className="text-sm text-agentic-text-secondary">Processed</div></div>
                    <div className="bg-agentic-gray-50 rounded-lg p-4"><div className="text-2xl font-bold text-agentic-accent-yellow">{stats.pending}</div><div className="text-sm text-agentic-text-secondary">Processing</div></div>
                    <div className="bg-agentic-gray-50 rounded-lg p-4"><div className="text-2xl font-bold text-agentic-accent-red">{stats.errors}</div><div className="text-sm text-agentic-text-secondary">Errors</div></div>
                </div>

                {/* Upload / Add URL buttons */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 w-full lg:w-auto">
                        <Button tone="custom" onClick={() => setShowUploadModal(true)} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border hover:bg-gray-50">
                            <CloudArrowUpIcon className="h-4 w-4"
                            /> Upload Document
                        </Button>
                        <Button tone="custom" onClick={() => setUrlType('url')} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border hover:bg-gray-50">
                            <GlobeAltIcon className="h-4 w-4" /> Add URL
                        </Button>
                        <Button tone="custom" onClick={() => setUrlType('sitemap')} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border hover:bg-gray-50">
                            <GlobeAltIcon className="h-4 w-4" /> Add sitemap.xml
                        </Button>
                    </div>
                </div>

                <input ref={fileInputRef} type="file" multiple accept=".pdf,.txt,image/*" hidden onChange={(e) => onDropFiles(e.target.files)} />

                {/* drag & drop area (preserved) */}
                {!(urlType === 'url' || urlType === 'sitemap' || urlType === 'video') && (
                    <div
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => { e.preventDefault(); onDropFiles(e.dataTransfer.files); }}
                        className="rounded-xl border border-dashed p-6 text-center text-sm text-agentic-text-secondary bg-agentic-gray-50"
                    >
                        Drag & drop PDF, TXT, or images here, or <Button tone="custom" className="text-agentic-primary-blue underline" onClick={() => fileInputRef.current?.click()}>browse</Button>
                    </div>
                )}

                {/* URL input */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    <div className="lg:col-span-3 flex items-center gap-2">
                        <SelectField tone="default" value={urlType} onChange={(e) => setUrlType(e.target.value as KBType)} className="px-2 py-2 rounded-lg border">
                            <option value="url">Web URL</option>
                            <option value="sitemap">Sitemap.xml</option>
                        </SelectField>
                        <TextField tone="default-focus-primary"
                            value={urlValue}
                            onChange={(e) => setUrlValue(e.target.value)}
                            placeholder={urlType === 'sitemap' ? 'https://example.com/sitemap.xml' : 'https://example.com/...'}
                            className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                        />
                        <Button tone="primary-fade" onClick={addUrl} className="px-4 py-2 rounded-lg">Add</Button>
                    </div>
                </div>

                {/* Documents list */}
                <div className="bg-white border rounded-lg">
                    <div className="px-6 py-4 border-b border-agentic-gray-200 flex items-center justify-between gap-3">
                        <h4 className="font-medium text-agentic-text-primary">Documents</h4>
                        <div className="w-full max-w-xs">
                            <TextField tone="default-focus-primary"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search documents..."
                                className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                            />
                        </div>
                    </div>
                    <div className="divide-y divide-agentic-gray-200">
                        {items.map((doc: any, i) => (
                            <div key={doc.id} className="px-6 py-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <TypeBadge type={doc.type} />
                                    <div>
                                        <div className="font-medium text-agentic-text-primary">{doc.name} {doc?.clientName ? `(${doc?.clientName})` : ""}</div>
                                        <div className="text-xs text-agentic-text-secondary flex items-center gap-4">
                                            {doc.size && <span>{doc.size}</span>}
                                            <span>Updated {doc.lastUpdated}</span>
                                            {doc.sitemapSummary && doc.embeddingStatus !== 'pending' && (
                                                <span>
                                                    {doc.sitemapSummary.succeeded} of {doc.sitemapSummary.totalUrls} pages scraped
                                                    {doc.sitemapSummary.failed > 0 && ` · ${doc.sitemapSummary.failed} failed`}
                                                </span>
                                            )}
                                        </div>
                                        <DocumentErrors doc={doc} />
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <StatusBadge embeddingStatus={doc.embeddingStatus} />

                                    {doc.embeddingStatus === "completed" && (
                                        <TrainingBadge id={doc.id} agentInstanceId={doc.agentInstanceId} onClick={() => handleTrainingClick(doc.id, doc.embeddingStatus, doc.agentInstanceId)} />
                                    )}

                                    {doc.embeddingStatus === "failed" && doc.agentInstanceId === agent?.id && (
                                        <Button tone="muted-text"
                                            aria-label="Retry processing"
                                            disabled={retryingId === doc.id}
                                            onClick={() => retryFailedDocument(doc.id)}
                                        >
                                            {retryingId === doc.id ? 'Retrying…' : 'Retry'}
                                        </Button>
                                    )}

                                    <EyeIcon className="w-5 h-5" style={{ cursor: "pointer" }} onClick={async () => {
                                        let url = doc.remoteUrl;
                                        if(doc.s3ObjectData){
                                            url = await fetchImageUrl(doc.s3ObjectData);
                                        }
                                        if(url) window.open(url, "_blank")
                                    }} />

                                    {(
                                        (doc.embeddingStatus === "completed" && doc.agentInstanceId) ||
                                        doc.embeddingStatus === "failed"
                                    ) && (
                                        <Button tone="muted-text"
                                           
                                            aria-label="Remove"
                                            onClick={() => removeDocument(doc.id)}
                                        >
                                            <XMarkIcon className="h-5 w-5" />
                                        </Button>
                                    )}

                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <ConfirmationDialog
                    isOpen={confirmOpen}
                    onClose={handleCancelRetrain}
                    onConfirm={handleConfirmRetrain}
                    title="Confirm Train"
                    message="Are you sure you want to train agent with the selected document?"
                    confirmText="Train"
                    cancelText="Cancel"
                    loading={retraining}
                    variant="info"
                />
            </div>
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.txt,image/*"
                hidden
                onChange={(e) => {
                    if (e.target.files) {
                        const newFiles = Array.from(e.target.files);
                        setSelectedFiles(prev => [...prev, ...newFiles]);
                    }
                }}
            />
            {showUploadModal && (
                <Overlay tone="dim-40" className="fixed inset-0 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl w-full max-w-lg p-6 shadow-xl space-y-6">

                        <h2 className="text-xl font-semibold">Upload Documents</h2>

                        {/* Selected Documents List */}
                        <div className="border rounded-lg p-4 bg-gray-50 max-h-52 overflow-auto">
                            {selectedFiles.length === 0 ? (
                                <p className="text-gray-500 text-sm">No documents selected yet.</p>
                            ) : (
                                selectedFiles.map((file, idx) => (
                                    <div key={idx} className="flex items-center justify-between py-1">
                                        <span className="text-sm">{file.name}</span>
                                        <Button tone="custom"
                                            onClick={() =>
                                                setSelectedFiles((prev) => prev.filter((_, i) => i !== idx))
                                            }
                                            className="text-red-500 text-xs hover:underline"
                                        >
                                            Remove
                                        </Button>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Add More Documents */}
                        <Button tone="custom"
                            className="px-4 py-2 text-sm rounded-lg border w-full flex items-center justify-center gap-2 hover:bg-gray-50"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <CloudArrowUpIcon className="h-4 w-4" /> Add Documents
                        </Button>

                        {/* Tenant */}
                        <div className="space-y-1">
                            <label className="text-sm text-gray-700 font-medium">Tenant</label>
                        <div className="w-full border rounded-lg px-3 py-2 bg-gray-50 text-gray-800">
                            {tenantName || 'Current tenant unavailable'}
                        </div>
                        <div className="text-xs text-gray-500">
                            Target collection: <span className="font-medium">{tenantId ? `${tenantId}_KB` : 'unavailable'}</span>
                        </div>
                        <p className="text-xs text-gray-500">
                            Documents are uploaded against the active tenant. A client selection is not required here.
                        </p>
                        </div>

                        {/* Submit */}
                        <Button tone="custom"
                            disabled={selectedFiles.length === 0}
                            onClick={() => {
                                setShowUploadModal(false);
                                handleUploadFilesSequential(selectedFiles as any);

                                setSelectedFiles([]);
                            }}
                            className={`w-full py-2 rounded-lg text-white ${selectedFiles.length === 0
                                ? "bg-gray-400"
                                : "bg-agentic-primary-blue hover:bg-agentic-primary-blue/90"
                                }`}
                        >
                            Submit
                        </Button>

                        {/* Close */}
                        <Button tone="custom"
                            onClick={() => setShowUploadModal(false)}
                            className="w-full text-center text-sm text-gray-500 hover:underline mt-1"
                        >
                            Cancel
                        </Button>
                    </div>
                </Overlay>
            )}
        </>
    );
};

export default KnowledgeBaseTab;
